import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { isAlive } from 'webapp/utils/common';

import type { Section as IndexSection } from 'webapp/components/metric-library/index';
import type { Section as SourceMetricsSection } from 'webapp/components/metric-library/source/tabs/metrics';
import type { Section as SourceTemplatesSection } from 'webapp/components/metric-library/source/tabs/templates';

import type { Metric } from 'webapp/models-custom';
import type { AddToHomeAction } from 'webapp/models-custom/batch-select';
import BatchSelect from 'webapp/models-custom/batch-select';

type Action = AddToHomeAction | 'addSelectedToBoard' | 'addSelectedToFavorites';

export interface SectionBase {
	id: IndexSection | SourceMetricsSection | SourceTemplatesSection;
	title: string;
	initialRowCount: number;
	loadMoreRowCount?: number;
	onItemsPerRowCalculated?: (c: number) => void;
}

export interface Args {
	onSourceChange: (sourceKey: string) => void;
	onAddMetricsToBoard: (metrics: Metric[]) => void;
}

export default abstract class MetricLibraryBaseComponent<T extends Args> extends Component<T> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	batchSelect: BatchSelect<Metric>;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.batchSelect = new BatchSelect<Metric>({
			getAllItems: () => {
				return [];
			},
			getOptions: () => {
				return this.batchOptions;
			},
			doBatchAction: (actionName) => {
				this.doBatchAction(actionName as Action);
				return Promise.resolve();
			},
		});
	}

	get batchOptions() {
		const selectedLength = this.selectedItems.length;
		return [
			...(this.currentUserService.hasHome ? [this.batchSelect.addToHomeOption] : []),
			...this.mmMetricsService.getBatchActionOptions(selectedLength, false),
		];
	}

	get batchButtonContainerNode() {
		return isAlive(this) && this.selectedItems.length
			? this.navigationService.batchButtonContainerNode
			: null;
	}

	get selectedItems(): Metric[] {
		return this.batchSelect.selectedItems;
	}

	@action
	doBatchAction(actionName: Action) {
		return this[actionName]();
	}

	@action
	batchAddToHome() {
		void this.mmMetricsService.addMetricsToHome(this.selectedItems);
	}

	@action
	addSelectedToBoard() {
		this.args.onAddMetricsToBoard(this.selectedItems);
	}

	@action
	addSelectedToFavorites() {
		// Only offered while mmMetricsService.showFavoriteAction is on; favoriting moves onto the registry in M2.
	}
}
